import React, {Component} from "react"
import PropTypes from "prop-types"
import styled from "styled-components"

import id from "@irati/utils/id"
import {injectIntl, FormattedMessage} from "@irati/utils/lang/"
import ToggleTagOnCommentMutation from "@irati/insights/apollo/mutation/comment/toggleTag"
import cookieHelper from "@irati/utils/cookies"
import Button from "@irati/ui/src/Button"
import {LatoLight} from "@irati/ui/src/global/fonts"
import {DropdownOverflow} from "@irati/ui/src/Dropdown"
import {IconTypes} from "@irati/ui/src/Icon"

import BookmarkRenderer from "./BookmarkRenderer"
import {BOOKMARK_COOKIE_REMOVE, BOOKMARK_TYPE} from "../../../utils/constants/comment"
import AddBookmarkModal from "../AddBookmarkModal"
import RemoveBookmarkModal from "../RemoveBookmarkModal"
import AnalysisCommentsConsumer from "../AnalysisCommentsConsumer"


const buttonStyle = `
  white-space: normal;
  box-shadow: none;
  ${LatoLight};
  padding: 10px 0;
  font-size: 10px;
  text-align: left;
`

const Cell = styled.div`
  padding-right: 2px;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
`

const DropdownWrapper = styled.div`
  padding-bottom: 5px;
  display: inline-flex;
`

const StyledButton = styled(Button)`
  ${buttonStyle}
`

const StyledButtonWithIcon = styled(Button)`
  ${buttonStyle}
`

const BookmarkWrapper = styled.div`
  max-height: 350px;
  overflow-y: auto;
`

class BookmarkCellRenderer extends Component {
  constructor(props) {
    super(props)
    this.addBookmarkModal = React.createRef()
    this.removeBookmarkModal = React.createRef()
    this.dropdown = React.createRef()
    this.state = {note: null}
  }

  getBookmark = () => {
    const {original: comment} = this.props
    return comment.tags.find((tag) => tag.type === BOOKMARK_TYPE)
  }

  handleDropdownClick = (toggleTag, hasBookmarks, isDemo) => {
    if (isDemo) return
    const bookmark = this.getBookmark()
    if (bookmark) {
      const cookie = cookieHelper.getItem(BOOKMARK_COOKIE_REMOVE)
      if (!cookie) {
        return this.toggleRemoveModal()
      } else {
        toggleTag()
      }
    } else if (!hasBookmarks) {
      return this.toggleAddModal()
    }
  }

  handleSubmit = (note) => {
    this.hasToggledNewNote = false
    this.setState({note})
    return this.dropdown.current
      && this.dropdown.current.toggle()
  }

  toggleRemoveModal = () => {
    return this.removeBookmarkModal.current
      && this.removeBookmarkModal.current.toggleModal()
  }

  toggleAddModal = () => {
    return this.addBookmarkModal.current
      && this.addBookmarkModal.current.toggleModal()
  }

  render() {
    const idNamespace = id("BookmarkCellRenderer", this.props)
    const {original: comment} = this.props
    const {note} = this.state
    const bookmark = this.getBookmark()
    const icon = bookmark ? IconTypes.bookmarkFill : IconTypes.bookmark
    const header = (
      <StyledButtonWithIcon onClick={this.toggleAddModal} icon="plus">
        <FormattedMessage id="insights.analysis.comments.addNewBookmarkButton" />
      </StyledButtonWithIcon>
    )
    return (
      <AnalysisCommentsConsumer>
        {({allBookmarks, isDemo}) => {
          return (
            <>
              <ToggleTagOnCommentMutation comment={comment} note={note}>
                {(toggleTag) => {
                  if (note && !this.hasToggledNewNote) {
                    this.hasToggledNewNote = true
                    toggleTag()
                  }
                  return (
                    <Cell id={idNamespace}>
                      <DropdownWrapper onClick={() => this.handleDropdownClick(toggleTag, allBookmarks.length > 0, isDemo)}>
                        <DropdownOverflow
                          ref={this.dropdown}
                          disabled={!allBookmarks.length || !!bookmark || isDemo}
                          header={header}
                          id={idNamespace}
                          icon={icon}
                          pullRight>
                          <BookmarkWrapper>
                            {allBookmarks.map((item, i) => {
                              return (
                                <StyledButton
                                  full
                                  onClick={() => this.handleSubmit(item.value)}
                                  key={i}>{item.value}
                                </StyledButton>
                              )
                            })}
                          </BookmarkWrapper>
                        </DropdownOverflow>
                      </DropdownWrapper>
                      <BookmarkRenderer tags={comment.tags} />
                    </Cell>
                  )
                }}
              </ToggleTagOnCommentMutation>
              <AddBookmarkModal ref={this.addBookmarkModal} comment={comment} />
              <RemoveBookmarkModal ref={this.removeBookmarkModal} comment={comment} />
            </>
          )
        }}
      </AnalysisCommentsConsumer>
    )
  }
}

BookmarkCellRenderer.propTypes = {
  formatMessage: PropTypes.func.isRequired,
  original: PropTypes.object
}

BookmarkCellRenderer.defaultProps = {
  original: {}
}

export default injectIntl(BookmarkCellRenderer)
